'use client';

import React, {useCallback, useEffect, useMemo, useRef, useState,} from 'react';
import {useBalloonEventsData} from '../contexts/EventData';
import {usePartners} from '../contexts/Partners';
import {ZeppelinContainer} from '../components/ZeppelinContainer';
import {ZeppelinContainerV2} from '../components/ZeppelinContainerV2';
import {HotspotsLayer} from '../components/HotspotsLayer';
import {AllBalloonsLayer} from '../components/AllBalloonsLayer';
import {HOTSPOT_DISPLAY_MODE} from '../hooks/useMapHotspots';
import {QueryFeed} from '../components/QueryBadge';
import { Link } from "react-router-dom";
import {useBalloonEventsOptions} from '../contexts/EmbedOptions';
import {Spinner, useQuery} from 'react-ui';
import dayjs from 'dayjs';
import {FilterLabelCarousel} from '../components/FilterLabelCarousel';
import {FilterToolbar} from "./blocks/FilterToolbar";
import {EventsList} from "../components/EventsList";
import {BalloonDetails} from "../components/EventDetails.tsx";
import {ProfileDetailPage} from "./ProfileDetailPage.tsx";
import {UserScopeHeader} from "../components/UserScopeHeader";
import {PartnerScopeDetailPage} from "./PartnerScopeDetailPage.tsx";
import {PartnerScopeHeader} from "../components/PartnerScopeHeader";
import {BannerContainerV2} from '../components/BannerContainerV2';

const MemoizedSpinner = React.memo(function MemoizedSpinner({show}: { show: boolean }) {
    if (!show) return null;
    return (
        <div className="absolute top-[50pt] left-1/2 transform -translate-x-1/2 animate-opacity-up">
            <Spinner/>
        </div>
    );
});

export default function BalloonEventsUI() {
    const {selectedEvent, loading, openedEvent} = useBalloonEventsData();
    const { queries, removeQueryById} = useQuery();
    const memoizedQueries = useMemo(() => queries, [queries]);
    const {
        showZeppelins,
        useCategorySearch,
        hideUI,
    } = useBalloonEventsOptions();

    const {sponsors, regionalPartners} = usePartners();

    const [showSpinner, setShowSpinner] = useState(false);

    const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        if (loading) {
            timerRef.current = setTimeout(() => setShowSpinner(true), 1000);
        } else setShowSpinner(false);
    }, [loading]);

/*if(map && coords && coords.x && coords.x < 1100 && !isMobile) {
            map.style.transform = 'translateX(650px)'
            map.style.transition = 'transform 0.3s ease-out'
        }*/
    const regionalPartnerContainerStyle = openedEvent ? {
        transform: 'translateX(690px)',
        transition: 'transform 0.3s ease-out'
    } : {}

    // hideUI: render only the balloon layer over the map — no panel, banners,
    // zeppelins, category selector, detail pages or query feed.
    if (hideUI) {
        return HOTSPOT_DISPLAY_MODE === 'dots' ? <AllBalloonsLayer /> : <HotspotsLayer />;
    }

    return (
        <div className="w-full h-full desktop-ui">
            <MemoizedSpinner show={showSpinner}/>
            <BalloonsPanel/>
            {useCategorySearch && (
                <div
                    className="category-selector balloon-map-topbar absolute top-0 left-[390px] right-0 flex justify-center pt-1 z-0 pointer-events-none"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0))'
                    }}
                >
                    <div className="pointer-events-auto slide-in-top w-full">
                        <FilterLabelCarousel/>
                    </div>
                </div>
            )}
            {selectedEvent && (
                <BalloonDetails selectedEvent={selectedEvent}/>
            )}
            {/* Rendered after the category-selector (z-0) so the profile pane stacks
                above it, mirroring the event-details pane. */}
            <ProfileDetailPage/>
            <PartnerScopeDetailPage/>
            <BannerContainerV2
                variant="desktop"
                className="banners flex flex-row items-end px-4 gap-3 absolute bottom-4 z-10 left-[390px]"
            />

            {showZeppelins && <ZeppelinContainer sponsors={sponsors}/>}
            {showZeppelins && <ZeppelinContainerV2/>}
            {HOTSPOT_DISPLAY_MODE === 'dots' ? <AllBalloonsLayer /> : <HotspotsLayer />}
            <div className="hidden flex-col z-50 absolute bottom-0 w-full pointer-events-none">
                <div className="flex self-end md:mr-8 mr-2">
                    <QueryFeed queries={memoizedQueries} removeQuery={removeQueryById}/>
                </div>
            </div>
        </div>
    );
}

export const BalloonsPanel = () => {
    const {selectedEvent, selectEvent, events, allFilteredEvents, loading} = useBalloonEventsData();
    const {addQuery, query} = useQuery();
    const memoizedQuery = useMemo(() => query, [query]);
    const {
        useDateSearch,
        useCategorySearch,
        useTextSearch,
        showCreatorLink,
        creatorURL,
    } = useBalloonEventsOptions();

    const {regionalPartners} = usePartners();
    const handleSetSearchText = useCallback(
        (text: string) => addQuery({text, id: 'text'}),
        [addQuery]
    );

    return <div className="balloons-panel w-[390px] h-full bg-white/90 backdrop-blur-md shadow-[2px_0px_6px_2px_rgba(0,0,0,0.20)] absolute top-0 left-0 flex flex-col justify-start slide-in-left z-1">
        <UserScopeHeader />
        <PartnerScopeHeader />
        <FilterToolbar
            useCategorySearch={useCategorySearch || false}
            useDateSearch={useDateSearch || false}
            memoizedQuery={memoizedQuery}
            callbackfn={(d) => dayjs(d)}
            selectedDates={(d) => addQuery({id: 'dates', dates: d.map((d) => d.toDate())})}
            useTextSearch={useTextSearch || false}
            loading={loading}
            events={allFilteredEvents}
            selectEvent={selectEvent}
            selectedEvent={selectedEvent}
            searchText={handleSetSearchText}
            searchText1={(text) => addQuery({text, id: 'text'})}
            showCreatorLink={showCreatorLink || false}
            creatorURL={creatorURL}
        />
        <EventsList
            partners={regionalPartners}
            hasMore={false}
            isLoading={loading}
            onLoadMore={() => void 0}
            events={events}
        />
    </div>
}